import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Lock, MapPin, Pencil, Trash2, Upload } from "lucide-react";

import "../Styles/Profile.css";
import Header from "../Components/Header";
import DefaultAvatar from "../Components/DefaultAvatar";
import ArtistAvatar from "../Components/ArtistAvatar";
import FollowButton from "../Components/Profile/FollowButton";
import UserOverflowMenu from "../Components/Profile/UserOverflowMenu";
import InviteFriendsButton from "../Components/Profile/InviteFriendsButton";
import FavoriteArtistsEditor from "../Components/Profile/FavoriteArtistsEditor";
import FollowListModal from "../Components/Profile/FollowListModal";
import GoingOutStatusControl from "../Components/GoingOut/GoingOutStatusControl";
import { useAuth } from "../Services/AuthContext";
import {
  ACCEPTED_AVATAR_TYPES,
  MAX_AVATAR_BYTES,
  deleteAvatar,
  fetchProfile,
  saveFavoriteArtists,
  updateMe,
  uploadAvatar,
} from "../Services/SocialService";
import { timeAgo } from "../utils/format";
import { venuePath } from "../utils/routes";
import type { FavoriteArtist, FollowState, Profile as ProfileDTO } from "../data/types";

export default function Profile() {
  const { userId } = useParams();
  const { user, isLoading: isAuthLoading, isAuthenticated, refresh: refreshAuth } = useAuth();

  const [profile, setProfile] = useState<ProfileDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draftBio, setDraftBio] = useState("");
  const [draftAvatarUrl, setDraftAvatarUrl] = useState("");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [draftArtists, setDraftArtists] = useState<FavoriteArtist[]>([]);

  const [followListMode, setFollowListMode] =
    useState<"followers" | "following" | null>(null);

  // Utan :userId i rutten visar vi den inloggade användarens egen profil.
  const targetId = userId ?? user?.id ?? null;

  const load = useCallback(async () => {
    if (!targetId) return;

    setIsLoading(true);
    setError(null);

    try {
      setProfile(await fetchProfile(targetId));
    } catch (e) {
      console.error("profile fetch failed", e);
      setError("Could not load this profile.");
    } finally {
      setIsLoading(false);
    }
  }, [targetId]);

  useEffect(() => {
    load();
  }, [load]);

  // Byter man profil mitt i en redigering ska utkastet inte följa med.
  useEffect(() => {
    setIsEditing(false);
  }, [targetId]);

  const startEditing = () => {
    if (!profile) return;

    setDraftBio(profile.bio ?? "");
    setDraftAvatarUrl(profile.avatarUrl ?? "");
    setDraftArtists(profile.favoriteArtists);
    setIsEditing(true);
  };

  const handleAvatarFile = async (file: File) => {
    setAvatarError(null);

    // Validera lokalt först — annars laddar vi upp flera megabyte bara för
    // att få tillbaka ett 413.
    if (!ACCEPTED_AVATAR_TYPES.includes(file.type)) {
      setAvatarError("Use a JPEG, PNG or WebP image.");
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError("That image is too large. Max 4 MB.");
      return;
    }

    setIsUploadingAvatar(true);

    try {
      const url = await uploadAvatar(file);
      setDraftAvatarUrl(url);
      // Avataren syns i headern, så AuthContext måste veta direkt.
      await Promise.all([load(), refreshAuth()]);
    } catch (e) {
      console.error("avatar upload failed", e);
      setAvatarError("Could not upload that image.");
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarError(null);
    setIsUploadingAvatar(true);

    try {
      await deleteAvatar();
      setDraftAvatarUrl("");
      await Promise.all([load(), refreshAuth()]);
    } catch (e) {
      console.error("avatar delete failed", e);
      setAvatarError("Could not remove the image.");
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const saveProfile = async () => {
    if (!profile) return;

    setIsSaving(true);

    try {
      // Avataren sparas redan av POST/DELETE /me/avatar, så den ska inte
      // skickas med här — det skulle skriva över en nyss uppladdad bild.
      await updateMe({ bio: draftBio });

      await saveFavoriteArtists(draftArtists.map((a) => a.id));

      setIsEditing(false);
      await Promise.all([load(), refreshAuth()]);
    } catch (e) {
      console.error("profile save failed", e);
      setError("Could not save your profile.");
    } finally {
      setIsSaving(false);
    }
  };

  const onFollowChange = (next: FollowState) => {
    setProfile((current) => {
      if (!current) return current;

      const wasFollowing = current.followState === "following";
      const isFollowing = next === "following";

      return {
        ...current,
        followState: next,
        // Avföljning låser profilen igen — hämta om för att slippa visa
        // data som betraktaren inte längre har rätt till.
        counts: {
          ...current.counts,
          followers:
            current.counts.followers + (isFollowing ? 1 : 0) - (wasFollowing ? 1 : 0),
        },
      };
    });

    // Statusen avgör vad servern får visa, så vi hämtar om profilen.
    load();
  };

  if (!isAuthLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <main className="profile">
      <Header />

      {isLoading && !profile && (
        <p className="profile-status">Loading profile…</p>
      )}

      {error && !profile && <p className="profile-status">{error}</p>}

      {profile && (
        <>
          <section className="profile-header">
            <div className="profile-avatar">
              <DefaultAvatar
                name={profile.displayName}
                avatarUrl={profile.avatarUrl ?? undefined}
              />
            </div>

            <div className="profile-header-main">
              <div className="profile-title-row">
                <h1>{profile.displayName}</h1>

                {profile.followsYou && !profile.isSelf && (
                  <span className="profile-chip">Follows you</span>
                )}

                {!profile.canView && (
                  <span className="profile-chip profile-chip--locked">
                    <Lock size={12} strokeWidth={2.4} />
                    Private
                  </span>
                )}
              </div>

              {profile.canView ? (
                <p className="profile-bio">
                  {profile.bio || "No bio yet."}
                </p>
              ) : (
                <p className="profile-bio profile-bio--muted">
                  Follow to see posts, activity and the full artist list.
                </p>
              )}

              <div className="follow-wrapper">
                <button
                  type="button"
                  className="follow-item"
                  disabled={!profile.canView}
                  onClick={() => setFollowListMode("followers")}
                >
                  <span>Followers</span>
                  <div className="follow-count">{profile.counts.followers}</div>
                </button>

                <button
                  type="button"
                  className="follow-item"
                  disabled={!profile.canView}
                  onClick={() => setFollowListMode("following")}
                >
                  <span>Following</span>
                  <div className="follow-count">{profile.counts.following}</div>
                </button>
              </div>

              <div className="profile-actions">
                {profile.isSelf ? (
                  <>
                    <button
                      type="button"
                      className="follow-button"
                      onClick={isEditing ? () => setIsEditing(false) : startEditing}
                    >
                      <Pencil size={15} strokeWidth={2.2} />
                      {isEditing ? "Cancel" : "Edit profile"}
                    </button>

                    {/* Att hitta vänner ska inte kräva att man kan stava
                        till deras namn i sökrutan. */}
                    <InviteFriendsButton />
                  </>
                ) : (
                  <>
                    <FollowButton
                      userId={profile.id}
                      followState={profile.followState}
                      onChange={onFollowChange}
                    />

                    {/* Efter en blockering läses profilen om. Servern har
                        då rivit följningarna, så vyn faller tillbaka till
                        den låsta varianten av sig själv — klienten behöver
                        inte gissa vad som ska döljas. */}
                    <UserOverflowMenu
                      userId={profile.id}
                      displayName={profile.displayName}
                      onBlocked={load}
                    />
                  </>
                )}
              </div>

              {/* Egen profil: kvällssvaret, ändringsbart. Kartan har
                  slutat fråga efter att man svarat, och det ska finnas
                  ett ställe att ändra sig på som inte är en meny. */}
              {profile.isSelf && <GoingOutStatusControl variant="profile" />}

              {profile.canView && profile.activeCheckIn?.venue && (
                <div className="profile-checkin">
                  <MapPin size={14} strokeWidth={2.2} />
                  <span>
                    Checked in at{" "}
                    <Link to={venuePath(profile.activeCheckIn.venue.id)}>
                      <strong>{profile.activeCheckIn.venue.name}</strong>
                    </Link>{" "}
                    ·{" "}
                    {timeAgo(profile.activeCheckIn.createdAt)}
                  </span>
                </div>
              )}
            </div>
          </section>

          {isEditing && profile.isSelf ? (
            <section className="profile-edit">
              <h2>Edit profile</h2>

              <label className="profile-field">
                <span>Bio</span>
                <textarea
                  className="profile-input"
                  maxLength={280}
                  rows={3}
                  placeholder="What are you listening to these days?"
                  value={draftBio}
                  onChange={(e) => setDraftBio(e.target.value)}
                />
                <em>{draftBio.length}/280</em>
              </label>

              <div className="profile-field">
                <span>Profile picture</span>

                <div className="avatar-upload">
                  <div className="avatar-upload-preview">
                    <DefaultAvatar
                      avatarUrl={draftAvatarUrl || undefined}
                      name={profile.displayName}
                    />
                    {isUploadingAvatar && <div className="avatar-upload-busy" />}
                  </div>

                  <div className="avatar-upload-actions">
                    <button
                      type="button"
                      className="avatar-upload-button"
                      disabled={isUploadingAvatar || isSaving}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={15} strokeWidth={2.2} />
                      {isUploadingAvatar
                        ? "Uploading…"
                        : draftAvatarUrl
                          ? "Change picture"
                          : "Upload picture"}
                    </button>

                    {draftAvatarUrl && (
                      <button
                        type="button"
                        className="avatar-remove-button"
                        disabled={isUploadingAvatar || isSaving}
                        onClick={removeAvatar}
                      >
                        <Trash2 size={15} strokeWidth={2.2} />
                        Remove
                      </button>
                    )}

                    <em className="avatar-upload-hint">JPEG, PNG or WebP. Max 4 MB.</em>
                  </div>
                </div>

                {avatarError && <p className="form-error">{avatarError}</p>}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_AVATAR_TYPES.join(",")}
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    // Nollställ så att samma fil kan väljas igen efter ett fel.
                    e.target.value = "";
                    if (file) void handleAvatarFile(file);
                  }}
                />
              </div>

              <div className="profile-field">
                <span>Favourite artists</span>
                <FavoriteArtistsEditor
                  artists={draftArtists}
                  onChange={setDraftArtists}
                />
              </div>

              <div className="profile-edit-actions">
                <button
                  type="button"
                  className="follow-button"
                  disabled={isSaving}
                  onClick={saveProfile}
                >
                  {isSaving ? "Saving…" : "Save changes"}
                </button>

                <button
                  type="button"
                  className="profile-ghost-button"
                  disabled={isSaving}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </div>
            </section>
          ) : (
            <div className="body-wrapper">
              <section className="activity">
                <h2 className="profile-section-title">Activity</h2>

                {!profile.canView ? (
                  <div className="profile-locked">
                    <Lock size={22} strokeWidth={1.8} />
                    <p>This profile is private</p>
                    <span>
                      {profile.followState === "requested"
                        ? "Your follow request is waiting to be approved."
                        : "Send a follow request to see posts and check-ins."}
                    </span>
                  </div>
                ) : profile.posts && profile.posts.length > 0 ? (
                  <div className="profile-posts">
                    {profile.posts.map((post) => (
                      <article key={post.id} className="profile-post">
                        {post.track.albumCover && (
                          <img
                            className="profile-post-cover"
                            src={post.track.albumCover}
                            alt=""
                          />
                        )}

                        <div className="profile-post-text">
                          <strong>{post.track.title}</strong>
                          <small>{post.track.artist}</small>

                          {post.caption && <p>{post.caption}</p>}

                          <em>
                            {timeAgo(post.createdAt)} · {post.likes} likes ·{" "}
                            {post.comments} comments
                          </em>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="profile-empty">Nothing shared yet.</p>
                )}
              </section>

              <section className="favorite-artists">
                <h2 className="profile-section-title">
                  {profile.canView ? "Favourite artists" : "Top artists"}
                </h2>

                {profile.favoriteArtists.length === 0 ? (
                  <p className="profile-empty">No favourite artists yet.</p>
                ) : (
                  <ol className="artist-list">
                    {profile.favoriteArtists.map((artist, index) => (
                      <li key={artist.id} className="artist-row">
                        <span className="artist-rank">{index + 1}</span>

                        <ArtistAvatar
                          name={artist.name}
                          seed={artist.id}
                          size={38}
                        />

                        <span className="artist-name">{artist.name}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </div>
          )}
        </>
      )}

      {followListMode && profile && (
        <FollowListModal
          userId={profile.id}
          mode={followListMode}
          onClose={() => setFollowListMode(null)}
        />
      )}
    </main>
  );
}
